/*
 * Ryan O'Dowd
 * 2024-10-02
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  ImageLibraryOptions,
  launchImageLibrary,
} from 'react-native-image-picker';
import React, {
  useState,
} from 'react';
import {
  fetchProfilePictureUrl,
  updateSchoolInfo,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import FormControl from '@components/common/FormControl';
import IconButton from '@components/common/IconButton';
import Image from '@components/Photo/Image';
import PaymentMethodDialog from './PaymentMethodDialog';
import Select from '@components/common/Select';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import {
  View,
} from 'react-native';
import fb from '@utils/firebase';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const SCHOOL_LOGO_PLACEHOLDER_IMAGE = require('@assets/img/avatar.png'); // @TODO: replace with school (jaguar)

const IMAGE_OPTIONS: ImageLibraryOptions = {
  mediaType: 'photo',
};

const USA = 'United States';

const cardImages = { // @TODO: all of these consistent names with what comes back from stripe?
  generic: '/img/credit_cards/generic.svg',
  amex: '/img/credit_cards/amex.svg',
  diners: '/img/credit_cards/diners.svg',
  discover: '/img/credit_cards/discover.svg',
  elo: '/img/credit_cards/elo.svg',
  hiper: '/img/credit_cards/hiper.svg',
  hipercard: '/img/credit_cards/hipercard.svg',
  jcb: '/img/credit_cards/jcb.svg',
  maestro: '/img/credit_cards/maestro.svg',
  mastercard: '/img/credit_cards/mastercard.svg',
  mir: '/img/credit_cards/mir.svg',
  paypal: '/img/credit_cards/paypal.svg',
  unionpay: '/img/credit_cards/unionpay.svg',
  visa: '/img/credit_cards/visa.svg',
};

const EditSchoolDialog = (props: EditSchoolDialogProps) => {
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();

  const updateSchoolInfoInFlight = hooks.useTypedSelector((state: any) => state.loadingStates.updateSchoolInfo);

  const [_schoolName, setSchoolName] = useState(props.school.name);
  const [_schoolWebsite, setSchoolWebsite] = useState(props.school.website_url || '');
  const [_country, setCountry] = useState(props.school.address?.country || '');
  const [_street, setStreet] = useState(props.school.address?.street || '');
  const [_city, setCity] = useState(props.school.address?.city || '');
  const [_state, setState] = useState(props.school.address?.state || '');
  const [_zip, setZip] = useState(props.school.address?.zip || '');

  const [_isStripeDialogOpen, setIsStripeDialogOpen] = useState(false);

  const [_isUploadingImage, setIsUploadingImage] = useState(false);

  const logoUrl = hooks.useGetPhotoUrl(`school_logo::${props.school.id}`); // @TODO: this format is too magical

  function _imagePickerCallback(response, pickerType) {
    if (!response.didCancel && response.error) {
      fb.log(fb.crashlytics, `image-picker (${pickerType}) error: \`${response.error}\``);
      fb.recordError(fb.crashlytics, response.error);
    } else if (!response.didCancel) {
      const image = response.assets[0];
      utilities.uploadPhoto(
        image.uri,
        globals.profilePictureSize, // @TODO: is this appropriate for school logos as well?
        `${globals.apiUrl}/schools/${props.school.id}/logo`,
        () => setIsUploadingImage(true),
        () => {
          dispatch(fetchProfilePictureUrl(props.school.id, true));
          setIsUploadingImage(false);
        },
      );
    }
  }

  const launchImageLibraryWrapper = () => {
    return launchImageLibrary(IMAGE_OPTIONS, (response) => _imagePickerCallback(response, 'library'));
  };


  const defaultPaymentMethodInfo = props.school.billing?.default_payment_method ?? {}; // @TODO: better null checking...or maybe shouldn't need null checking?

  const unsavedEditsExist = false; // @TODO: to school name, website, or address

  const [_validationErrors, setValidationErrors] = useState({
    schoolName: false,
    websiteUrl: false,
    logoUrl: false,
    country: false,
    street: false,
    city: false,
    state: false,
    zip: false,
    billing: false,
  });

  return (
    <Dialog
      style={styles.container}
      visible={true}
      onDismiss={() => props.onClose()} // @TODO: ask about unsaved changes
    >
      <Dialog.Title>{i18n('editSchoolInfo')}</Dialog.Title>
      {/* @TODO: dashlane is way off here... */}
      <Dialog.Content>
        <TextInput
          value={_schoolName}
          mode='outlined'
          onChangeText={(text) => setSchoolName(text)}
          label={i18n('schoolName')}
          isAlert={_validationErrors.schoolName}
        />
        <TextInput
          value={_schoolWebsite}
          mode='outlined'
          onChangeText={(text) => setSchoolWebsite(text)}
          label={i18n('schoolWebsite')}
          isAlert={_validationErrors.websiteUrl}
        />
        <FormControl
          style={styles.logoFormControl}
          label='School logo' // @TODO: i18n
          isAlert={_validationErrors.logoUrl}
          disabled={updateSchoolInfoInFlight}
        >
          <Image
            style={styles.schoolLogoImage}
            resizeMode='cover'
            source={!logoUrl ? SCHOOL_LOGO_PLACEHOLDER_IMAGE : {
              uri: logoUrl,
              priority: 'normal',
            }}
            onError={() => {
              // probably still in redux but not in the FastImage cache, so refetch here
              dispatch(fetchProfilePictureUrl(props.school.id, true));
            }}
            username={props.school.name}
          />
          <Button
            mode='outlined'
            theme={styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primary}
            onPress={() => launchImageLibraryWrapper()}
            disabled={_isUploadingImage || updateSchoolInfoInFlight}
            loading={_isUploadingImage}
          >
            Upload logo{/* @TODO: i18n */}
          </Button>
        </FormControl>
        <FormControl
          style={styles.addressFormControl}
          label='School address' // @TODO: i18n
          disabled={updateSchoolInfoInFlight}
        >
          <Select
            value={_country}
            options={countriesOptions}
            onChange={(text) => setCountry(text)}
            label='Country'// @TODO: i18n
            isAlert={_validationErrors.country}
          />
          <TextInput
            value={_street}
            mode='outlined'
            onChangeText={(text) => setStreet(text)}
            label={(_country !== '' && _country !== USA) ? 'Full address' : 'Street'}// @TODO: i18n
            isAlert={_validationErrors.street}
          />
          {(_country === '' || _country === USA) &&
            <React.Fragment>
              <TextInput
                value={_city}
                mode='outlined'
                onChangeText={(text) => setCity(text)}
                label='City'// @TODO: i18n
                isAlert={_validationErrors.city}
              />
              <View style={styles.addressRow}>
                <Select
                  value={_state}
                  options={statesOptions}
                  onChange={(text) => setState(text)}
                  label='State'// @TODO: i18n
                  isAlert={_validationErrors.state}
                />
                <TextInput
                  value={_zip}
                  mode='outlined'
                  onChangeText={(text) => setZip(text)}
                  label='Zip'// @TODO: i18n
                  isAlert={_validationErrors.zip}
                />
              </View>
            </React.Fragment>
          }
        </FormControl>
        {/* @TODO: upload logo */}
        <FormControl
          style={styles.cardInfoFormControl}
          label='Default payment method' // @TODO: i18n
          isAlert={_validationErrors.billing}// @TODO: no longer used; should be !props.school.billing?.stripe_payment_method_id once account is established?
          disabled={updateSchoolInfoInFlight}
        >
          {props.school.billing?.stripe_payment_method_id // @TODO: probably shouldn't need the null check?
            ? (
              <React.Fragment>
                <View style={styles.cardInfoWrapper}>
                  <View style={styles.ccImageAndExpiration}>
                    <Image
                      style={styles.ccImage}
                      source={{
                        uri: cardImages[defaultPaymentMethodInfo.brand] || cardImages.generic,
                        priority: 'normal',
                      }}
                      resizeMode='contain'
                    />
                    <Text style={styles.expirationText}>exp: {defaultPaymentMethodInfo.expiry_month}/{defaultPaymentMethodInfo.expiry_year}</Text>
                  </View>
                  <Text>**** **** **** {defaultPaymentMethodInfo.last4}</Text>
                </View>
                <View style={styles.buttonAndMessages}>
                  <IconButton
                    style={styles.editButton}
                    icon='pencil'
                    disabled={unsavedEditsExist || updateSchoolInfoInFlight}
                    iconColor={styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primary}
                    onPress={() => setIsStripeDialogOpen(true)}
                  />
                </View>
              </React.Fragment>
            )
            : (
              // there's no card on file...capture new payment info
              <Button
                theme={styles.colors.accent}
                onPress={() => setIsStripeDialogOpen(true)}
              >
                Set default payment method{/* @TODO: i18n */}
              </Button>
            )
          }
        </FormControl>
        {/* @TODO: don't need this in v1
        <Text>{`Auto renew is on: ${!!school.billing.auto_renew}.`}</Text>
        */}
        {_isStripeDialogOpen && <PaymentMethodDialog onClose={() => setIsStripeDialogOpen(false)} schoolId={props.school.id} />}
      </Dialog.Content>
      <Dialog.Actions errorMessage={Object.values(_validationErrors).some(Boolean) ? 'Please fill out all required fields.' : ''}>{/* @TODO: i18n */}
        <Button
          mode='text'
          onPress={() => props.onClose()}
        >
          {i18n('cancel')}
        </Button>
        <Button
          disabled={updateSchoolInfoInFlight}
          loading={updateSchoolInfoInFlight}
          onPress={async () => {
            const currErrorStatuses = {
              schoolName: !_schoolName,
              websiteUrl: !_schoolWebsite, // @TODO: regex?
              logoUrl: !logoUrl, // @TODO: regex?
              country: !_country,
              street: !_street,
              city: (!_country || _country === USA) && !_city,
              state: (!_country || _country === USA) && !_state,
              zip: (!_country || _country === USA) && !_zip, // @TODO: regex?
            };
            setValidationErrors(currErrorStatuses);
            if (Object.values(currErrorStatuses).some(Boolean)) {
              // @TODO: theses shouldn't all be required on first open...maybe require only before paying? pass a prop to force fill out, and that's true when this component is rendered from the add-seats dialog but not from the main school page?
              return;
            }

            dispatch(updateSchoolInfo(
              props.school.id,
              {
                name: _schoolName,
                website_url: _schoolWebsite,
                address: {
                  country: _country,
                  street: _street,
                  city: _city,
                  state: _state,
                  zip: _zip,
                },
              },
              props.onClose,
            ));
          }}
        >
          {i18n('save')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

class EditSchoolDialogProps {
  onClose: () => void;
  school: {
    id: number;
    name: string;
    website_url: string;
    billing: any;
    address: any;
  }; // @TODO: school type
};

export default EditSchoolDialog;

const countries = [
  USA,
  'Afghanistan',
  'Albania',
  'Algeria',
  'American Samoa',
  'Andorra',
  'Angola',
  'Anguilla',
  'Antarctica',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Aruba',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bermuda',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Brunei Darussalam',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Cape Verde',
  'Cayman Islands',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Christmas Island',
  'Cocos (Keeling) Islands',
  'Colombia',
  'Comoros',
  'Democratic Republic of the Congo (Kinshasa)',
  'Congo, Republic of (Brazzaville)',
  'Cook Islands',
  'Costa Rica',
  "Côte D'ivoire (Ivory Coast)",
  'Croatia',
  'Cuba',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'East Timor (Timor-Leste)',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Ethiopia',
  'Falkland Islands',
  'Faroe Islands',
  'Fiji',
  'Finland',
  'France',
  'French Guiana',
  'French Polynesia',
  'French Southern Territories',
  'Gabon',
  'The Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Gibraltar',
  'Greece',
  'Greenland',
  'Grenada',
  'Guadeloupe',
  'Guam',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Holy See',
  'Honduras',
  'Hong Kong',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran (Islamic Republic of)',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Ivory Coast',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  "Korea, Democratic People's Rep. (North Korea)",
  'Korea, Republic of (South Korea)',
  'Kosovo Flag',
  'Serbian: Kosovo, Косово',
  'Kuwait',
  'Kyrgyzstan',
  "Lao, People's Democratic Republic",
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Macau',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Martinique',
  'Mauritania',
  'Mauritius',
  'Mayotte',
  'Mexico',
  'Micronesia, Federal States of',
  'Moldova, Republic of',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Montserrat',
  'Morocco',
  'Mozambique',
  'Myanmar, Burma',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'Netherlands Antilles',
  'New Caledonia',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'Niue',
  'North Macedonia',
  'Northern Mariana Islands',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestinian territories',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Pitcairn Island',
  'Poland',
  'Portugal',
  'Puerto Rico',
  'Qatar',
  'Reunion Island',
  'Romania',
  'Russian Federation',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovakia (Slovak Republic)',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Swaziland (Eswatini)',
  'Sweden',
  'Switzerland',
  'Syria, Syrian Arab Republic',
  'Taiwan (Republic of China)',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Tibet',
  'Timor-Leste (East Timor)',
  'Togo',
  'Tokelau',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Turks and Caicos Islands',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Vatican City State',
  'Venezuela',
  'Vietnam',
  'Virgin Islands (British)',
  'Virgin Islands (U.S.)',
  'Wallis and Futuna Islands',
  'Western Sahara',
  'Yemen',
  'Zambia',
  'Zimbabwe',
];

const countriesOptions = countries.map((country) => {
  return {
    label: country,
    value: country,
  };
});

const states = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'AS',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'GU',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'MP',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'TT',
  'UT',
  'VT',
  'VA',
  'VI',
  'WA',
  'WV',
  'WI',
  'WY',
];

const statesOptions = states.map((state) => {
  return {
    label: state,
    value: state,
  };
});
